import {weakMemo} from '@packages/utilities/memo';
import {PREBUILT_LAYOUTS_PLUGIN_ID} from './pluginIds';
import type {LoadedLibraries} from './types';

const pluginIdsOf = weakMemo(
  (libraries: LoadedLibraries): ReadonlySet<string> =>
    new Set(
      Object.keys(libraries).filter(
        (pluginId) => pluginId !== PREBUILT_LAYOUTS_PLUGIN_ID
      )
    )
);

const NO_LIBRARIES: ReadonlySet<string> = new Set();

/**
 * The plugin ids of a site's installed libraries.
 *
 * Slot restrictions are enforced only against components that could appear in
 * an allow-list -- the site's own, and an installed library's. A core plugin
 * registers components too, and the picker never offers those, so enforcing
 * against one would refuse it from every restricted slot with no way for an
 * author to permit it.
 *
 * Code libraries are included. Their components compile to ordinary plugin
 * components with element presets, so the panel lists them and the picker
 * offers them like any other library's.
 *
 * PrebuiltLayouts is skipped: it arrives here shaped like a library and is
 * not one.
 *
 * Memoized on the libraries object: this is read on every drag-over tick, and
 * a fresh Set each time would defeat any consumer comparing by identity.
 *
 * Its own module, deep-imported by the enforcement paths: reaching it through
 * a barrel drags selector modules into their load order, and those build
 * reselect selectors at module scope.
 */
export const selectLibraryPluginIds = (
  libraries: LoadedLibraries | undefined
): ReadonlySet<string> => (libraries ? pluginIdsOf(libraries) : NO_LIBRARIES);
